import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, RefreshCw, Smartphone, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix default marker icon path (CRA bundling) — same trick RoutePreviewMap uses.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const driverIcon = (initials, online) => L.divIcon({
  className: 'driver-heartbeat-marker',
  html: `<div style="
    width:34px;height:34px;border-radius:50%;
    background:${online ? '#10B981' : '#94A3B8'};color:#fff;
    display:flex;align-items:center;justify-content:center;
    font-weight:700;font-size:12px;letter-spacing:0.02em;
    border:3px solid #fff;box-shadow:0 2px 8px rgba(15,23,42,.35);
    font-family:Work Sans, sans-serif;
  ">${initials}</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

// ELD (truck) icon — distinct silhouette so admins can never confuse a
// truck-side location with a driver-phone location on the same map.
const truckIcon = (label) => L.divIcon({
  className: 'eld-truck-marker',
  html: `<div style="
    min-width:38px;height:26px;padding:0 8px;border-radius:6px;
    background:#002FA7;color:#fff;
    display:flex;align-items:center;justify-content:center;gap:4px;
    font-weight:800;font-size:11px;letter-spacing:0.02em;
    border:2px solid #fff;box-shadow:0 2px 8px rgba(15,23,42,.35);
    font-family:IBM Plex Sans, sans-serif;
  "><span style="font-size:12px;">🚛</span>${label || ''}</div>`,
  iconSize: [50, 26],
  iconAnchor: [25, 13],
});

const _initials = (name) => {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
};

const _fmtRelative = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

// Auto-fit the map to all driver markers whenever the set changes.
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 7);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
  }, [map, points]);
  return null;
};

/**
 * DriverHeartbeatMap — companion to SyncHealthWidget.
 * Renders a mini-map with a marker per driver whose PWA has sent a GPS
 * fix in the last 30 minutes. Marker color: green = online, gray =
 * heartbeat stale. Auto-refreshes every 60s.
 */
const DriverHeartbeatMap = () => {
  const [positions, setPositions] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Layer toggles — never disable a data SOURCE, just its rendering. Both
  // sources continue polling in the background so nothing is lost when
  // the operator hides a layer.
  const [showPhone, setShowPhone] = useState(true);
  const [showEld, setShowEld] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [phoneRes, eldRes] = await Promise.all([
        axios.get(`${API}/admin/driver-positions`, { withCredentials: true }),
        axios.get(`${API}/eld/vehicles`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPositions(Array.isArray(phoneRes.data?.positions) ? phoneRes.data.positions : []);
      setVehicles(Array.isArray(eldRes.data) ? eldRes.data : []);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Unable to load positions';
      setError(typeof msg === 'string' ? msg : 'Unable to load positions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60_000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const eldPoints = useMemo(
    () => vehicles.filter(
      (v) => v.latest_telemetry
        && typeof v.latest_telemetry.lat === 'number'
        && typeof v.latest_telemetry.lng === 'number',
    ),
    [vehicles],
  );

  const points = useMemo(
    () => {
      const arr = [];
      if (showPhone) {
        positions
          .filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number')
          .forEach((p) => arr.push([p.lat, p.lng]));
      }
      if (showEld) {
        eldPoints.forEach((v) => arr.push([v.latest_telemetry.lat, v.latest_telemetry.lng]));
      }
      return arr;
    },
    [positions, eldPoints, showPhone, showEld],
  );

  const totalSources = (showPhone ? positions.length : 0) + (showEld ? eldPoints.length : 0);

  return (
    <div
      data-testid="driver-heartbeat-map"
      className="bg-white border border-slate-200 rounded-md p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
            <MapPin className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <h2
              className="text-lg font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Live Fleet Map
            </h2>
            <p
              className="text-xs text-slate-500"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Phone GPS and ELD GPS — always shown as separate sources
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="driver-map-count"
            className="text-xs text-slate-500 hidden sm:inline"
          >
            {totalSources} pin{totalSources === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={fetchAll}
            aria-label="Refresh"
            data-testid="driver-map-refresh"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Source toggles — never disable the underlying data collection */}
      <div className="flex items-center gap-2 mb-3 text-xs">
        <button
          type="button"
          data-testid="map-toggle-phone"
          onClick={() => setShowPhone((v) => !v)}
          aria-pressed={showPhone}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border ${
            showPhone
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-white border-slate-200 text-slate-500'
          }`}
        >
          <Smartphone className="w-3.5 h-3.5" />
          <span className="font-semibold">Phone GPS</span>
          <span className="opacity-70">({positions.length})</span>
        </button>
        <button
          type="button"
          data-testid="map-toggle-eld"
          onClick={() => setShowEld((v) => !v)}
          aria-pressed={showEld}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border ${
            showEld
              ? 'bg-[#002FA7]/10 border-[#002FA7]/30 text-[#002FA7]'
              : 'bg-white border-slate-200 text-slate-500'
          }`}
        >
          <Truck className="w-3.5 h-3.5" />
          <span className="font-semibold">ELD GPS</span>
          <span className="opacity-70">({eldPoints.length})</span>
        </button>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center text-sm text-slate-500">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading map…
        </div>
      ) : error ? (
        <div
          data-testid="driver-map-error"
          className="h-64 flex items-center justify-center text-xs text-slate-500 border border-dashed border-slate-200 rounded-md"
        >
          Map unavailable ({error}).
        </div>
      ) : points.length === 0 ? (
        <div
          data-testid="driver-map-empty"
          className="h-64 flex flex-col items-center justify-center text-xs text-slate-500 border border-dashed border-slate-200 rounded-md gap-2"
        >
          <MapPin className="w-6 h-6 text-slate-300" />
          <div>No location fixes yet in the selected sources.</div>
          <div className="text-[10px] text-slate-400">
            Drivers need location permission on their PWA, or a truck needs an ELD device linked.
          </div>
        </div>
      ) : (
        <div
          className="rounded-md overflow-hidden border border-slate-200"
          style={{ height: 320 }}
          data-testid="driver-map-canvas"
        >
          <MapContainer
            center={points[0]}
            zoom={5}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={points} />
            {showPhone && positions.map((p) => (
              <Marker
                key={`phone-${p.id}`}
                position={[p.lat, p.lng]}
                icon={driverIcon(_initials(p.name), p.online)}
              >
                <Popup>
                  <div style={{ fontFamily: 'IBM Plex Sans, sans-serif', minWidth: 180 }}>
                    <div style={{ fontSize: 10, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      📱 Phone GPS
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A' }}>
                      {p.name}
                    </div>
                    <div style={{ fontSize: 11, color: p.online ? '#059669' : '#64748B', marginTop: 2 }}>
                      {p.online ? '● Online' : '○ Heartbeat stale'}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                      GPS captured {_fmtRelative(p.captured_at)}
                    </div>
                    {typeof p.accuracy_m === 'number' && (
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>
                        ±{Math.round(p.accuracy_m)}m
                      </div>
                    )}
                    {p.pending_ops_count > 0 && (
                      <div style={{ fontSize: 11, color: '#B45309', fontWeight: 600, marginTop: 4 }}>
                        {p.pending_ops_count} queued offline action{p.pending_ops_count === 1 ? '' : 's'}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
            {showEld && eldPoints.map((v) => (
              <Marker
                key={`eld-${v.truck_asset_id}`}
                position={[v.latest_telemetry.lat, v.latest_telemetry.lng]}
                icon={truckIcon(v.label ? v.label.slice(0, 8) : '')}
              >
                <Popup>
                  <div style={{ fontFamily: 'IBM Plex Sans, sans-serif', minWidth: 180 }}>
                    <div style={{ fontSize: 10, color: '#002FA7', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2, fontWeight: 700 }}>
                      🚛 ELD GPS
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A' }}>
                      {v.label || 'Truck'}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                      Provider: <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{v.eld_provider}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                      Captured {_fmtRelative(v.latest_telemetry.captured_at)}
                    </div>
                    {typeof v.latest_telemetry.speed_mph === 'number' && (
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>
                        {Math.round(v.latest_telemetry.speed_mph)} mph · engine {v.latest_telemetry.engine_state || '—'}
                      </div>
                    )}
                    {typeof v.latest_telemetry.odometer_miles === 'number' && (
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>
                        Odometer {Math.round(v.latest_telemetry.odometer_miles).toLocaleString()} mi
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default DriverHeartbeatMap;
